<template>
    <div :class="['cz__upload', { 'cz__option--selected': selected }]">
        <dropzone ref="dropzone" id="dropzone" :options="dropzoneOptions"
            @vdropzone-file-added="fileAdded"
            @vdropzone-sending="fileSending"
            @vdropzone-error="fileError"
            @vdropzone-success="fileSuccess"
        ></dropzone>

        <p v-if="uploadError" class="cz__upload-error">{{uploadError}}</p>

        <div class="cz__upload-requirements">
            Pick a PNG or JPG image that is
            <span v-for="(r, rIndex) in requirements"><!--
                --><span v-if="r.type === 'filesize'"><span v-if="rIndex > 0">,<span v-if="rIndex === requirements.length - 1"> and</span></span> at most {{r.value.toFixed(1).replace('.0', '')}}MB file size</span><!--
                --><span v-if="r.type === 'min-dimensions'"><span v-if="rIndex > 0">,<span v-if="rIndex === requirements.length - 1"> and</span></span> at least {{r.width}}x{{r.height}} image dimensions</span><!--
                --><span v-if="r.type === 'max-dimensions'"><span v-if="rIndex > 0">,<span v-if="rIndex === requirements.length - 1"> and</span></span> at most {{r.width}}x{{r.height}} image dimensions</span><!--
            --></span>.
        </div>
    </div>
</template>

<style lang="less">
    .cz__upload {
        width: 100%;

        .dropzone {
            width: 100%;
        }

        .dz-default {
            position: relative;

            height: 32px;

            padding: 0px 15px;
            color: #333;
            background-color: #f1f1f1;

            font-size: 12px;
            line-height: 32px;

            text-transform: uppercase;

            cursor: pointer;

            &:after {
                content: '';

                position: absolute;

                left: -1px;
                bottom: -8px;
                width: ~"calc(100% + 2px)";
                height: 3px;
                background: #000;
            }
        }

        .dz-preview {
            margin-top: 15px;
            margin-bottom: 15px;

            overflow: hidden;

            .dz-size {
                display: none;
            }

            .dz-filename {
                font-size: 14px;
            }

            .dz-image {
                float: left;

                margin-right: 10px;
            }

            .dz-success-mark {
                display: none;
            }

            .dz-error-mark {
                display: none;
            }

            .dz-progress {
                display: block;

                &:before {
                    content: 'Processing...';

                    display: block;

                    margin-top: 15px;

                    font-size: 14px;
                }
            }

            &.dz-complete {
                .dz-progress {
                    display: none;
                }
            }
        }
    }

    .cz__upload-requirements {
        margin-top: 15px;
        margin-bottom: 15px;

        font-size: 14px;
    }

    .cz__upload-error {
        font-size: 14px;

        color: #933;
    }
</style>

<script>
    import Dropzone from 'vue2-dropzone';

    export default {
        components: {
            Dropzone,
        },
        props: {
            controller: Object,
            blueprint: Object,
            option: Object,
            selected: Boolean,
            components: Array,
        },
        data() {
            let endpoint = '//api.images.drivecommerce.com/api/v1/';

            if (this.blueprint.imageRootUrl) {
                endpoint = this.blueprint.imageRootUrl.replace('http://', '//').replace('https://', '//');
            }

            return {
                dropzoneOptions: {
                    url: endpoint,
                    uploadMultiple: false,

                    dictDefaultMessage: 'Drop an image or tap to start upload',

                    acceptedFiles: '.png,.jpg,.jpeg,*.tiff',

                    maxFiles: 1,
                    maxFilesize: 1,

                    thumbnailWidth: 100,
                    thumbnailHeight: 100,
                },

                uploadError: null,

                currentFile: null,

                imageMinWidth: null,
                imageMaxWidth: null,
                imageMinHeight: null,
                imageMaxHeight: null,
                imageMaxSize: null,

                requirements: [],
            };
        },
        watch: {
            components() {
                this.updateWidget();
            },
        },
        mounted() {
            this.updateWidget();
        },
        methods: {
            updateWidget() {
                const selected = this.components.find(c => c.selected);

                const { custom } = selected.component;

                this.imageMaxSize = custom && custom['dynamic-image-upload-limit-filesize'] ? +custom['dynamic-image-upload-limit-filesize'] : null;

                this.imageMinHeight = custom && custom['dynamic-image-upload-limit-min-height'] ? +custom['dynamic-image-upload-limit-min-height'] : null;
                this.imageMaxHeight = custom && custom['dynamic-image-upload-limit-max-height'] ? +custom['dynamic-image-upload-limit-max-height'] : null;

                this.imageMinWidth = custom && custom['dynamic-image-upload-limit-min-width'] ? +custom['dynamic-image-upload-limit-min-width'] : null;
                this.imageMaxWidth = custom && custom['dynamic-image-upload-limit-max-width'] ? +custom['dynamic-image-upload-limit-max-width'] : null;

                this.dropzoneOptions.maxFile = this.imageMaxSize;

                this.requirements = [];

                if (this.imageMaxSize) {
                    this.requirements.push({
                        type: 'filesize',
                        value: this.imageMaxSize,
                    });
                }

                if (this.imageMinHeight && this.imageMinWidth) {
                    this.requirements.push({
                        type: 'min-dimensions',
                        width: this.imageMinWidth,
                        height: this.imageMinHeight,
                    });
                }

                if (this.imageMaxHeight && this.imageMaxWidth) {
                    this.requirements.push({
                        type: 'max-dimensions',
                        width: this.imageMaxWidth,
                        height: this.imageMaxHeight,
                    });
                }
            },

            fileAdded(file) {
                if (this.currentFile) {
                    this.$refs.dropzone.removeFile(this.currentFile);
                }

                this.currentFile = file;
            },

            fileSending(file, xhr, formData) {
                const selected = this.components.find(c => c.selected);

                const { custom } = selected.component;

                if (this.imageMinHeight && this.imageMinWidth) {
                    formData.set('min-width', this.imageMinWidth);
                    formData.set('min-height', this.imageMinHeight);
                }

                if (this.imageMaxHeight && this.imageMaxWidth) {
                    formData.set('max-width', this.imageMaxWidth);
                    formData.set('max-height', this.imageMaxHeight);
                }

                const fit = custom && custom['dynamic-image-upload-fit'] ? +custom['dynamic-image-upload-fit'] : null;
                const fitWidth = custom && custom['dynamic-image-upload-fit-width'] ? +custom['dynamic-image-upload-fit-width'] : null;
                const fitHeight = custom && custom['dynamic-image-upload-fit-height'] ? +custom['dynamic-image-upload-fit-height'] : null;

                if (fit && fitWidth && fitHeight) {
                    formData.set('fit', fit);
                    formData.set('fit-width', fitWidth);
                    formData.set('fit-height', fitHeight);
                }
            },

            fileError(file, message) {
                this.$refs.dropzone.removeFile(file);

                if (this.currentFile === file) {
                    this.currentFile = null;
                }

                this.uploadError = null;

                if (message && message.modelState && message.modelState['']) {
                    const msg = message.modelState[''];

                    if (Array.isArray(msg)) {
                        // eslint-disable-next-line
                        this.uploadError = msg[0];
                    } else {
                        this.uploadError = msg;
                    }
                }
            },

            fileSuccess(file, response) {
                this.uploadError = null;

                this.$emit('dynamicimage', {
                    title: file.name,
                    name: response.name,
                    url: response.imageUrl,
                    pngUrl: response.pngImageUrl,
                    jpgUrl: response.jpgImageUrl,
                    width: response.width,
                    height: response.height,
                });
            },

            personalize() {
                this.$emit('personalization', this.text);
            },
        },
    };
</script>